import { Box, Paper, Skeleton, Typography } from '@mui/material'
import { alpha } from '@mui/material/styles'
import GridViewIcon from '@mui/icons-material/GridView'
import { useTranslation } from 'react-i18next'

import type { TestTemplate } from '@/types'
import { TestCreateSection } from '../TestCreateSection'

export type TestTemplateId = number | 'none'

/** Псевдошаблон «настроить вручную»: в списке он стоит последним. */
type ManualTemplate = Omit<TestTemplate, 'id'> & { id: 'none' }

/**
 * Выбор шаблона настроек теста.
 *
 * «Без шаблона» — не отсутствие выбора, а полноценный вариант списка: учитель
 * должен видеть, что настраивает вручную, а не гадать, почему ни одна плитка
 * не подсвечена.
 */
export function TestTemplateSection({
  templates,
  loading,
  selectedTemplate,
  onTemplateChange,
}: {
  templates: TestTemplate[] | null
  loading: boolean
  selectedTemplate: TestTemplateId
  onTemplateChange: (id: TestTemplateId) => void
}) {
  const { t } = useTranslation()

  const manual: ManualTemplate = {
    id: 'none',
    slug: 'none',
    title: t('tests.createForm.templateNoneTitle'),
    description: t('tests.createForm.templateNoneDesc'),
    settings: {},
    is_system: true,
    sort: Number.MAX_SAFE_INTEGER,
  }

  return (
    <TestCreateSection
      id="template"
      icon={<GridViewIcon fontSize="small" />}
      title={t('tests.createForm.sectionTemplate')}
    >
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr 1fr', sm: '1fr 1fr' },
          gap: 1,
        }}
      >
        {loading
          ? Array.from({ length: 4 }, (_, i) => (
              <Paper key={i} variant="outlined" sx={{ p: 1.25 }}>
                <Skeleton variant="text" width="70%" height={20} />
                <Skeleton variant="text" width="90%" height={16} />
              </Paper>
            ))
          : [...(templates ?? []), manual].map((tpl) => {
              const selected = selectedTemplate === tpl.id
              return (
                <Paper
                  key={tpl.id}
                  variant="outlined"
                  sx={(theme) => ({
                    p: 1.25,
                    borderColor: selected ? theme.palette.primary.main : theme.palette.divider,
                    bgcolor: selected
                      ? alpha(theme.palette.primary.main, theme.palette.mode === 'light' ? 0.12 : 0.22)
                      : theme.palette.background.paper,
                    cursor: 'pointer',
                    transition: theme.transitions.create(['background-color', 'border-color'], {
                      duration: theme.transitions.duration.shorter,
                    }),
                  })}
                  onClick={() => onTemplateChange(tpl.id)}
                >
                  <Typography
                    variant="body2"
                    sx={(theme) => ({
                      fontWeight: 600,
                      color: selected ? theme.palette.primary.main : theme.palette.text.primary,
                    })}
                  >
                    {tpl.title}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={(theme) => ({
                      color: selected
                        ? theme.palette.mode === 'light'
                          ? theme.palette.primary.dark
                          : theme.palette.primary.light
                        : theme.palette.text.secondary,
                    })}
                  >
                    {tpl.description ?? ''}
                  </Typography>
                </Paper>
              )
            })}
      </Box>
    </TestCreateSection>
  )
}
